import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/contexts/LanguageContext";
import { pathFor, ROUTE_KEYS } from "@/i18n/routeMap";
import LanguageSwitcher from "@/components/layout/LanguageSwitcher";
import Logo from "@/components/Logo";

const NavLink = ({ to, children, onClick, testid }) => (
  <Link to={to} onClick={onClick} data-testid={testid}
    className="text-sm tracking-[0.18em] uppercase font-semibold text-[#F5F1EB] hover:text-[#C9A55A] transition-colors">
    {children}
  </Link>
);

export default function Header() {
  const { lang, t } = useLang();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  if (location.pathname.startsWith("/admin")) return null;

  return (
    <header className="fixed top-0 inset-x-0 z-50 glass-dark border-b border-[#2A2A2A]" data-testid="site-header">
      <div className="mx-auto max-w-7xl px-5 md:px-10 h-20 flex items-center justify-between">
        {/* Logo only — no extra text */}
        <Link to={pathFor(ROUTE_KEYS.HOME, lang)} data-testid="brand-logo" aria-label="Spain Luxury Rentals">
          <Logo size={48} />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-10">
          <NavLink to={pathFor(ROUTE_KEYS.HOME, lang)} testid="nav-catalog">{t.nav.catalog}</NavLink>
          <NavLink to={pathFor(ROUTE_KEYS.ROUTES, lang)} testid="nav-routes">{t.nav.routes}</NavLink>
          <NavLink to={pathFor(ROUTE_KEYS.CONTACT, lang)} testid="nav-contact">{t.nav.contact}</NavLink>
        </nav>

        <div className="flex items-center gap-4">
          <LanguageSwitcher />
          <button
            data-testid="menu-toggle"
            aria-label="Menu"
            className="md:hidden w-10 h-10 flex items-center justify-center border border-[#2A2A2A] text-[#F5F1EB]"
            onClick={() => setOpen((v) => !v)}>
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden border-t border-[#2A2A2A] bg-[#0C0C0C]"
            data-testid="mobile-menu">
            <div className="px-6 py-8 flex flex-col gap-6">
              <NavLink to={pathFor(ROUTE_KEYS.HOME, lang)} onClick={() => setOpen(false)} testid="mobile-nav-catalog">{t.nav.catalog}</NavLink>
              <NavLink to={pathFor(ROUTE_KEYS.ROUTES, lang)} onClick={() => setOpen(false)} testid="mobile-nav-routes">{t.nav.routes}</NavLink>
              <NavLink to={pathFor(ROUTE_KEYS.CONTACT, lang)} onClick={() => setOpen(false)} testid="mobile-nav-contact">{t.nav.contact}</NavLink>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
